import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { useDispatch } from "react-redux";
import { LayoutSplashScreen } from "../../../../_metronic/layout/core";
import { AuthModel, UserModel } from "./_models";
import * as authHelper from "./AuthHelpers";
import { refreshAuthSession } from "./_requests";
import { WithChildren } from "../../../../_metronic/helpers";
import { clearUserState } from "../../../../redux/features/user/_userAction";
import axios from "axios";
import { notify } from "../../../../utils/shared";

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: any | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => void;
};

const API_URL = process.env.REACT_APP_API_URL;
const AUTH_ENDPOINT = `${API_URL}/auth`;

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const dispatch = useDispatch();
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = async () => {
    let user: any = localStorage.getItem("kt-auth-react-v");
    user = JSON.parse(user);

    try {
      const res = await axios.get(`${AUTH_ENDPOINT}/logout`, {
        headers: { authorization: user.token },
      });
      notify(res?.data?.responseMessage || "Logged out successfully", "success");
    } catch (error: any) {
      // ignore logout API errors
    }

    saveAuth(undefined);
    dispatch(clearUserState());
    setCurrentUser(undefined);
  };

  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, saveAuth, setCurrentUser } = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  useEffect(() => {
    const requestUser = async (currentAuth: any) => {
      try {
        if (didRequest.current) {
          return;
        }
        didRequest.current = true;

        const token = currentAuth?.accessToken || currentAuth?.token;
        if (!token) {
          logout();
          return;
        }

        // Refresh role permissions from server so admin role edits apply
        // on reload without forcing the virtual user to log in again.
        try {
          const { data } = await refreshAuthSession(token);
          const session = data?.data || {};
          const refreshedAuth = {
            ...currentAuth,
            accessToken: token,
            token,
            email: session.email || currentAuth.email,
            isVirtualUser: session.isVirtualUser ?? currentAuth.isVirtualUser,
            role: session.role ?? currentAuth.role,
            permissions:
              session.permissions !== undefined
                ? session.permissions
                : currentAuth.permissions,
            parentAccount: session.parentAccount ?? currentAuth.parentAccount,
            parentAccountName:
              session.parentAccountName ?? currentAuth.parentAccountName,
            parentId: session.parentId ?? currentAuth.parentId,
          };
          saveAuth(refreshedAuth);
          setCurrentUser(refreshedAuth);
        } catch (sessionError) {
          // If session refresh fails but token still exists, keep local auth.
          setCurrentUser(currentAuth);
        }
      } catch (error) {
        logout();
      } finally {
        setShowSplashScreen(false);
      }
    };

    if (auth && (auth.accessToken || (auth as any).token)) {
      requestUser(auth as any);
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
